// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Img, { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage"; // Foundation
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner"; // Foundation
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import React, { useEffect, useState } from "react"; // Foundation
import { connect } from "react-redux";
import { css } from "styled-components";

/* Foundation ---> */
import translate from "@insite/client-framework/Translate";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useCallback, useEffect, useState } from "react";
/* <--- Foundation */

/* Bunzl ---> */
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
/* <--- Bunzl */
interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    // Store Custom Code
    onClick?: (event?: React.MouseEvent<any>) => void;
    extendedStyles?: ProductPrimaryImageStyles;
    // Foundation
    showLoader?: boolean;
    containerRef?: React.RefObject<HTMLDivElement>;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // Foundation
    loadingWrapper?: InjectableCss;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {},
    hiddenWrapper: {
        css: css`
            display: none;
        `,
    },
    spinner: {
        size: 40,
        css: css`
            position: absolute;
            top: calc(50% - ${pxToRem(20)});
            left: calc(50% - ${pxToRem(20)});
        `,
    },
    image: {},
    img: {
        css: css`
            cursor: pointer;
        `,
    },
};

const ProductPrimaryImage = ({
    productSettings,
    product,
    image,
    useLargeImage,
    onClick,
    extendedStyles,
    // Foundation
    showLoader,
}: Props) => {
    // Store Custom Code - for dynamic styles
    const styles = useMergeProps(productPrimaryImageStyles, extendedStyles);

    // Foundation --->
    const [lastHeight, setLastHeight] = useState(300);
    const [lastInstance, setLastInstance] = useState<HTMLElement | null>(null);
    const setRefHandler = (instance: HTMLElement | null) => {
        if (instance) {
            setLastInstance(instance);
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const onLoadHandler = useCallback(() => {
        setIsLoading(false);
        setTimeout(() => {
            if (lastInstance && lastInstance.clientHeight > 0) {
                setLastHeight(lastInstance.clientHeight);
            }
        }, 100);
    }, [setIsLoading, lastInstance, setLastHeight]);

    useEffect(() => {
        if (image?.imageType === "Static") {
            setIsLoading(true);
        }
    }, [image?.id]);
    // <--- Foundation

    if (!product) {
        return null;
    }

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    // bunzl deleted code - passed onClick instead of a build in handler so that we can pass the event object implicitely

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath // Foundation -- fix default img
        : image.mediumImagePath || image.largeImagePath; // Foundation -- fix default img
    if (!path) {
        return null;
    }

    // const isDefaultPath = path === COMMON.defaultProductImagePath; // Foundation -- fix default img
    const customIsLoading = isLoading && showLoader; // Foundation
    return (
        <>
            {/* Foundation */}
            {customIsLoading && (
                <StyledWrapper {...styles.loadingWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            {/* Foundation */}
            {image.imageType === "Static" && (
                <StyledWrapper
                    {...(customIsLoading ? styles.hiddenWrapper : styles.centeringWrapper)} /* Foundation */
                    onClick={onClick} /* Bunzl */
                    className="product-primary-image-wrapper" /* Bunzl */
                >
                    <VisuallyHidden>
                        {translate(`Clickable image. This action will enlarge ${product.productTitle} image`)}
                    </VisuallyHidden>
                    {/* Keep LazyImage for style reason */}
                    <LazyImage
                        className="product-primary-image"
                        {...styles.image}
                        key={image.id}
                        src={path}
                        altText={image.imageAltText}
                        imgProps={{ ref: setRefHandler }}
                        onLoad={onLoadHandler}
                        onError={onLoadHandler}
                        data-test-selector="productDetails_mainImage"
                        onKeyPress={imageWrapperKeyHandler}
                        tabIndex={0}
                    />
                </StyledWrapper>
            )}
            {/* Bunzl click handler */}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: lastHeight }} onClick={onClick}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </>
    );
};

// Store Custom Code
export const defaultPrimaryImageCss = `.product-primary-image-wrapper {
}
.product-primary-image {
}
`;

export default connect(mapStateToProps)(ProductPrimaryImage);
